/**
 * Fee calculation service for trial entry registration.
 *
 * Pure functions — no DI framework needed.
 * Computes entry fees based on grid selections, stock-type pricing,
 * pricing tier, and applicable discounts.
 */

export type PricingTier = 'PreEntry' | 'DayOf' | 'Closed';

export type StockType = 'Sheep' | 'Cattle' | 'Ducks' | 'Mixed';

export interface StockTypePricing {
  Sheep: number;
  Cattle: number;
  Ducks: number;
  Mixed: number;
}

export interface FeeSchedule {
  preEntryFees: StockTypePricing;
  dayOfFees: StockTypePricing;
  weekendDiscountPercent: number | null;
  weekendDiscountAmount: number | null;
  juniorHandlerDiscountPercent: number | null;
  juniorHandlerDiscountAmount: number | null;
}

export interface FeeLineItem {
  stockType: StockType;
  trialCount: number;
  unitPrice: number;
  lineTotal: number;
}

export interface FeeDiscount {
  description: string;
  amount: number;
}

export interface FeeBreakdown {
  lineItems: FeeLineItem[];
  subtotal: number;
  discounts: FeeDiscount[];
  total: number;
  pricingTier: PricingTier;
}

export interface GridSelection {
  row: string;
  col: string;
  value: string | null;
}

const STOCK_TYPES: StockType[] = ['Sheep', 'Cattle', 'Ducks', 'Mixed'];

function isStockType(row: string): row is StockType {
  return STOCK_TYPES.includes(row as StockType);
}

/**
 * Returns true if the column represents a trial date (e.g. DATE1_TRIAL1).
 * Only date column selections incur fees; class columns (STD, OPN, etc.) do not.
 */
export function isTrialDateColumn(col: string): boolean {
  return /^DATE\d+/i.test(col);
}

/**
 * Count the number of distinct days represented in the grid selections.
 * Column names like DATE1_TRIAL1, DATE2_TRIAL1 indicate different days.
 */
function countDistinctDays(selections: GridSelection[]): number {
  const days = new Set<string>();
  for (const sel of selections) {
    if (!sel.value) continue;
    const match = sel.col.match(/^DATE(\d+)/i);
    if (match) {
      days.add(match[1]);
    }
  }
  return days.size;
}

/**
 * Calculate fee breakdown from grid selections and fee schedule.
 */
export function calculateFees(
  selections: GridSelection[],
  feeSchedule: FeeSchedule,
  pricingTier: PricingTier,
  isJuniorHandler: boolean
): FeeBreakdown {
  const activeSelections = selections.filter((s) => s.value != null && s.value !== '');
  const priceMap = pricingTier === 'PreEntry' ? feeSchedule.preEntryFees : feeSchedule.dayOfFees;

  // Only count trial date column selections for fees — class columns (STD, OPN, etc.) don't incur charges
  const trialDateSelections = activeSelections.filter((s) => isTrialDateColumn(s.col));
  const countsByStock = new Map<StockType, number>();
  for (const sel of trialDateSelections) {
    if (!isStockType(sel.row)) continue;
    countsByStock.set(sel.row, (countsByStock.get(sel.row) ?? 0) + 1);
  }

  // Build line items
  const lineItems: FeeLineItem[] = [];
  for (const stockType of STOCK_TYPES) {
    const count = countsByStock.get(stockType) ?? 0;
    if (count === 0) continue;
    const unitPrice = priceMap[stockType];
    lineItems.push({
      stockType,
      trialCount: count,
      unitPrice,
      lineTotal: count * unitPrice,
    });
  }

  const subtotal = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);

  // Apply discounts
  const discounts: FeeDiscount[] = [];

  // Weekend discount: applied when selecting trials on multiple days
  const distinctDays = countDistinctDays(activeSelections);
  if (distinctDays > 1) {
    if (feeSchedule.weekendDiscountPercent != null && feeSchedule.weekendDiscountPercent > 0) {
      const amount = Math.round(subtotal * (feeSchedule.weekendDiscountPercent / 100) * 100) / 100;
      if (amount > 0) {
        discounts.push({
          description: `Weekend discount (${feeSchedule.weekendDiscountPercent}%)`,
          amount,
        });
      }
    } else if (feeSchedule.weekendDiscountAmount != null && feeSchedule.weekendDiscountAmount > 0) {
      discounts.push({
        description: 'Weekend discount',
        amount: feeSchedule.weekendDiscountAmount,
      });
    }
  }

  // Junior handler discount
  if (isJuniorHandler) {
    const afterWeekend = subtotal - discounts.reduce((s, d) => s + d.amount, 0);
    if (feeSchedule.juniorHandlerDiscountPercent != null && feeSchedule.juniorHandlerDiscountPercent > 0) {
      const amount = Math.round(afterWeekend * (feeSchedule.juniorHandlerDiscountPercent / 100) * 100) / 100;
      if (amount > 0) {
        discounts.push({
          description: `Junior handler discount (${feeSchedule.juniorHandlerDiscountPercent}%)`,
          amount,
        });
      }
    } else if (feeSchedule.juniorHandlerDiscountAmount != null && feeSchedule.juniorHandlerDiscountAmount > 0) {
      discounts.push({
        description: 'Junior handler discount',
        amount: feeSchedule.juniorHandlerDiscountAmount,
      });
    }
  }

  const totalDiscounts = discounts.reduce((sum, d) => sum + d.amount, 0);
  const total = Math.max(0, subtotal - totalDiscounts);

  return {
    lineItems,
    subtotal,
    discounts,
    total,
    pricingTier,
  };
}

/**
 * Build a default fee schedule with common pricing.
 */
export function createDefaultFeeSchedule(overrides?: Partial<FeeSchedule>): FeeSchedule {
  return {
    preEntryFees: { Sheep: 25, Cattle: 25, Ducks: 20, Mixed: 30 },
    dayOfFees: { Sheep: 35, Cattle: 35, Ducks: 30, Mixed: 40 },
    weekendDiscountPercent: null,
    weekendDiscountAmount: null,
    juniorHandlerDiscountPercent: null,
    juniorHandlerDiscountAmount: null,
    ...overrides,
  };
}
